import { useEffect, useState } from "react";
import AutomationService, { AutomationSchema } from "@/app/_services/AutomationService";

/**
 * The schema the whole builder renders itself from.
 *
 * Events, actions, operators and their config fields all come from the server, so
 * adding an event or an action on the backend needs no change here — the event picker,
 * the condition path list, the action dropdown and its config form are all generated.
 * This file knows the *shape* of a spec, never the specs themselves.
 *
 * Cached in a module-level promise rather than per-hook state: several components on a
 * detail page need it at once, and it never changes within a session.
 */
let cached: Promise<AutomationSchema> | null = null;

function loadSchema() {
  if (!cached) {
    cached = AutomationService.getSchemaAsync().catch((error) => {
      // Clear the cache on failure, otherwise one flaky request poisons the page for
      // the rest of the session.
      cached = null;
      throw error;
    });
  }
  return cached;
}

export function useAutomationSchema() {
  const [schema, setSchema] = useState<AutomationSchema | null>(null);
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    loadSchema()
      .then((data) => {
        if (active) setSchema(data);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { schema, isLoading };
}

/** Label lookups, so tables and headers can show "Order purchased" not "order.purchase". */
export function useSchemaLabels() {
  const { schema } = useAutomationSchema();
  return {
    eventLabel: (key: string) =>
      schema?.events.find((e) => e.key === key)?.label ?? key,
    actionLabel: (key: string) =>
      schema?.actions.find((a) => a.key === key)?.label ?? key,
  };
}
